import { createContext, useContext, useState, ReactNode } from 'react';

type SubscriptionTier = 'free' | 'professional' | 'enterprise';

interface User {
  id: string;
  name: string;
  email: string;
  apiKey: string;
  tier: SubscriptionTier;
  publicAnalysesUsed: number;
  publicAnalysesLimit: number;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => void;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Mock user data
const mockUsers = [
  {
    id: '1',
    name: 'Dr. Sarah Chen',
    email: 'sarah.chen@research.edu',
    password: 'demo123',
    apiKey: 'omni_sk_1a2b3c4d5e6f7g8h9i0j',
    tier: 'professional' as SubscriptionTier,
    publicAnalysesUsed: 15,
    publicAnalysesLimit: 100
  },
  {
    id: '2',
    name: 'Dr. John Smith',
    email: 'john.smith@lab.org',
    password: 'demo123',
    apiKey: 'omni_sk_9z8y7x6w5v4u3t2s1r0q',
    tier: 'free' as SubscriptionTier,
    publicAnalysesUsed: 8,
    publicAnalysesLimit: 10
  }
];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);

  const login = async (email: string, password: string): Promise<boolean> => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    
    const foundUser = mockUsers.find(
      u => u.email === email && u.password === password
    );

    if (foundUser) {
      setUser({
        id: foundUser.id,
        name: foundUser.name,
        email: foundUser.email,
        apiKey: foundUser.apiKey,
        tier: foundUser.tier,
        publicAnalysesUsed: foundUser.publicAnalysesUsed,
        publicAnalysesLimit: foundUser.publicAnalysesLimit
      });
      return true;
    }
    
    return false;
  };

  const logout = () => {
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isAuthenticated: !!user }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
